// pages/tasks/TaskCreateModal.jsx
//
// Popup wrapper around the SAME TaskCreateForm used by standalone
// task-create pages. No duplicate task business logic or validation.

import React, { useState } from "react";
import { Modal } from "react-bootstrap";
import TaskCreateForm from "./TaskCreateForm";

export default function TaskCreateModal({
  show,
  mode,
  onClose,
  onCreated,
}) {
  const [dirty, setDirty] = useState(false);

  const isComplaint = mode === "complaint";

  const eyebrow = isComplaint ? "IT Support" : "Work Tracking";
  const title = isComplaint ? "Raise Complaint" : "Create Work Ticket";
  const subtitle = isComplaint
    ? "Tell us what issue you are facing."
    : "Track the work you are planning or completing.";

  const requestClose = () => {
    if (
      dirty &&
      !window.confirm(
        "Discard this form? Your entered details will be lost."
      )
    ) {
      return;
    }

    setDirty(false);
    onClose?.();
  };

  // Fires the moment the task is actually created (before any attachment
  // step) so the list page refreshes right away -- the complaint must
  // appear in Staff -> My Complaints immediately, not only once the modal
  // finally closes.
  const handleCreated = (task) => {
    onCreated?.(task);
  };

  // Fires once the whole flow is done and the modal should close --
  // immediately for a task type with no attachment step, or after the
  // user clicks Done on the inline success+attachment screen.
  const handleFinished = () => {
    setDirty(false);
    onClose?.();
  };

  return (
    <Modal
      show={show}
      onHide={requestClose}
      centered
      scrollable
      backdrop
      keyboard
      className="tcm-modal"
      // React-Bootstrap portals the modal straight to document.body by
      // default -- OUTSIDE the .portal-theme (or Admin's .admin-teal-theme)
      // wrapper div that defines --primary/--admin-primary. Without this,
      // every var(--admin-primary, var(--primary, <fallback>)) below and in
      // TaskCreateForm.jsx's own <style> block silently resolves to its
      // hardcoded fallback color instead of the real brand token -- on the
      // Jaipuria portal that fallback is a leftover green, not maroon/gold.
      // Anchoring the portal inside the themed subtree lets it inherit the
      // real tokens like any other themed page content.
      container={() => document.querySelector(".portal-theme, .admin-teal-theme") || document.body}
    >
      <Modal.Header closeButton className="tcm-header">
        <div>
          <div className="tcm-eyebrow">{eyebrow}</div>

          <Modal.Title className="tcm-title">
            {title}
          </Modal.Title>

          <p className="tcm-subtitle">{subtitle}</p>
        </div>
      </Modal.Header>

      <Modal.Body className="tcm-body">
        <TaskCreateForm
          mode={mode}
          embedded
          onDirtyChange={setDirty}
          onCancel={requestClose}
          onCreated={handleCreated}
          onSuccess={handleFinished}
        />
      </Modal.Body>

      <style>{`
        .tcm-modal .modal-dialog {
          max-width: 680px;
          max-height: calc(100vh - 40px);
        }

        .tcm-modal .modal-content {
          max-height: calc(100vh - 40px);
          border-radius: 16px;
          border: none;
          overflow: hidden;
          box-shadow: 0 20px 60px rgba(15, 23, 42, 0.22);
        }

        .tcm-header {
          padding: 16px 20px;
          align-items: flex-start;
          border-bottom: 1px solid #f1f5f9;
          background: #fafcfb;
        }

        .tcm-header .btn-close {
          font-size: 10px;
          margin-top: 3px;
          opacity: 0.5;
        }

        .tcm-header .btn-close:hover {
          opacity: 0.9;
        }

        .tcm-eyebrow {
          margin-bottom: 2px;
          font-size: 9px;
          font-weight: 800;
          text-transform: uppercase;
          letter-spacing: 1px;
          color: var(--admin-primary, var(--primary, #6d1530));
        }

        .tcm-title {
          font-size: 16px;
          font-weight: 800;
          line-height: 1.2;
          color: #0f172a;
        }

        .tcm-subtitle {
          margin: 2px 0 0;
          font-size: 11.5px;
          color: #64748b;
        }

        .tcm-body {
          padding: 16px 20px 0;
          font-size: 13px;
        }

        .tcm-body .row.g-3 {
          --bs-gutter-x: 0.75rem;
          --bs-gutter-y: 0.65rem;
        }

        .tcm-body .form-label {
          margin-bottom: 0.25rem;
          font-size: 11.5px;
          font-weight: 700;
          letter-spacing: 0.2px;
          color: #334155;
        }

        .tcm-body .form-control,
        .tcm-body .form-select {
          min-height: 36px;
          padding: 0.4rem 0.6rem;
          font-size: 12.5px;
          border-radius: 8px;
          border-color: #e2e8f0;
        }

        .tcm-body .form-control:focus,
        .tcm-body .form-select:focus {
          border-color: var(--admin-primary, var(--primary, #6d1530));
          box-shadow: 0 0 0 3px var(--admin-primary-soft, var(--primary-light, rgba(109, 21, 48, 0.15)));
        }

        .tcm-body textarea {
          min-height: 60px;
          font-size: 12.5px;
          resize: vertical;
        }

        .tcm-body .form-text {
          margin-top: 0.2rem;
          font-size: 10px;
          color: #94a3b8;
        }

        .tcm-body .fw-semibold {
          font-size: 12px;
        }

        .tcm-body .small {
          font-size: 11px;
        }

        /* Quick-create buttons */
        .tcm-body .task-quick-create {
          padding: 0;
          border: 0;
          background: transparent;
          font-size: 10.5px;
          font-weight: 700;
          color: var(--admin-primary, var(--primary, #6d1530));
          cursor: pointer;
        }

        .tcm-body .task-quick-create:hover {
          color: var(--admin-primary-hover, var(--primary-hover, #4a0e22));
          text-decoration: underline;
        }

        .tcm-body .task-quick-create:disabled {
          opacity: 0.4;
          cursor: not-allowed;
          text-decoration: none;
        }

        /* Sticky action bar */
        .tcm-body .tcm-actions {
          position: sticky;
          bottom: 0;
          left: 0;
          right: 0;
          z-index: 5;
          margin-top: 14px !important;
          padding: 12px 0 16px;
          border-top: 1px solid #f1f5f9;
          background: #fff;
        }

        .tcm-body .tcm-actions .btn {
          padding: 0.5rem 1.1rem;
          font-size: 12.5px;
          font-weight: 700;
          border-radius: 9px;
        }

        .tcm-body .tcm-actions .unit-btn-primary {
          border: none !important;
          background: var(--admin-primary, var(--primary, #6d1530)) !important;
          color: #fff !important;
          box-shadow: 0 3px 10px var(--admin-primary-soft, var(--primary-light, rgba(109, 21, 48, 0.25)));
        }

        .tcm-body .tcm-actions .unit-btn-primary:hover {
          background: var(--admin-primary-hover, var(--primary-hover, #4a0e22)) !important;
        }

        .tcm-body .tcm-actions .btn-outline-secondary {
          border-color: #e2e8f0;
          color: #475569;
        }

        .tcm-body .tcm-actions .btn-outline-secondary:hover {
          background: #f8fafc;
          color: #0f172a;
        }

        /* TAT preview */
        .tcm-body .p-3.border.rounded {
          background: #fafcfb;
          border-color: #eef2f0 !important;
        }

        @media (max-width: 768px) {
          .tcm-modal .modal-dialog {
            max-width: 94vw;
          }

          .tcm-body {
            padding: 14px 14px 0;
          }
        }
      `}</style>
    </Modal>
  );
}